import type { Theme } from '@jgp-er-dev/themes'
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { v4 as uuid } from 'uuid'

import ExportButton from '../../components/ExportButton'
import PreSaveModal from '../../components/ExportButton/PreSaveModal'

import { getChartSVG, genPPT } from '../../utils/export'

interface ExportContext {
  addToQueue: (chart: Highcharts.Chart) => void
  addImageToQueue: (image: string, title: string) => void
  exportToPPT: (args: {
    imagesPerPage: number
    theme: Theme
    title?: string
    subject?: string
  }) => Promise<void>
}

interface Props {
  theme: Theme
  children?: React.ReactNode
  className?: string
}

const ExportContext = createContext<ExportContext>({} as ExportContext)

export const ExportContextProvider: React.FC<Props> = (props) => {
  const svgs = useRef<Record<string, string>>({})
  const [exportList, setExportList] = useState<{ id: string; name: string }[]>(
    []
  )
  const [openModal, setOpenModal] = useState<boolean>(false)

  const addToQueue = useCallback((chart: Highcharts.Chart) => {
    const id = uuid()
    svgs.current[id] = getChartSVG(chart)
    setExportList((curList) =>
      curList.concat({
        id,
        name: chart.options.exporting?.chartOptions?.title?.text ?? 'Gráfico',
      })
    )
  }, [])

  const addImageToQueue = useCallback((image: string, title: string) => {
    const id = uuid()
    svgs.current[id] = image
    setExportList((curList) => curList.concat({ id, name: title }))
  }, [])

  const exportToPPT = useCallback(
    async (args: {
      imagesPerPage: number
      theme: Theme
      title?: string
      subject?: string
    }) => {
      await genPPT({
        svgs: exportList.map(({ id }) => svgs.current[id]),
        theme: args.theme,
        imagesPerPage: args.imagesPerPage,
        title: args.title,
        subject: args.subject,
      })
      svgs.current = {}
      setExportList([])
    },
    [exportList]
  )

  const value = useMemo(() => ({ addToQueue, addImageToQueue, exportToPPT }), [
    addToQueue,
    addImageToQueue,
    exportToPPT,
  ])

  return (
    <ExportContext.Provider value={value}>
      <PreSaveModal
        className="w-72"
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={(info) =>
          exportToPPT({
            imagesPerPage: info.itemsPerPage,
            theme: props.theme,
            title: info.title,
            subject: info.subject,
          })
        }
      />
      <ExportButton
        className={props.className}
        exportList={exportList}
        setExportList={setExportList}
        onExportClick={() => setOpenModal(true)}
      />
      {props.children}
    </ExportContext.Provider>
  )
}

export const useExport = () => useContext(ExportContext)
